import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useMemo } from "react";

// Filter operator types matching FilterPanel component
export type FilterOperator =
  | "is"
  | "is_not"
  | "is_any_of"
  | "is_not_any_of"
  | "is_none_of"
  | "includes_all"
  | "excludes_all"
  | "before"
  | "after"
  | "between"
  | "not_between"
  | "contains"
  | "not_contains"
  | "starts_with"
  | "ends_with"
| "equals"
  | "not_equals"
  | "greater_than"
  | "less_than"
  | "greater_than_or_equals"
  | "less_than_or_equals"
  | "overlaps"
  | "includes"
  | "excludes"
  | "includes_all_of"
  | "includes_any_of"
  | "empty"
  | "not_empty";

// Filter value types
export type FilterValue = string | number | boolean | Date | null;

// Individual filter definition
export interface TableFilter {
  id: string;
  tableName: string;
  columnName: string;
  operator: FilterOperator;
  values: FilterValue[];
}

// State shape
interface FilterState {
  filters: TableFilter[];
}

const initialState: FilterState = {
  filters: [],
};

// Helper to generate unique filter ID
const generateFilterId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

const filterSlice = createSlice({
  name: "filter",
  initialState,
  reducers: {
    // Add a new filter
    addFilter: (
      state,
      action: PayloadAction<Omit<TableFilter, "id">>
    ) => {
      const newFilter: TableFilter = {
        ...action.payload,
        id: generateFilterId(),
      };
      state.filters.push(newFilter);
    },

    // Add a filter with a specific ID (useful for controlled scenarios)
    addFilterWithId: (state, action: PayloadAction<TableFilter>) => {
      // Remove existing filter with same ID if present
      state.filters = state.filters.filter((f) => f.id !== action.payload.id);
      state.filters.push(action.payload);
    },

    // Update an existing filter by ID
    updateFilter: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<Omit<TableFilter, "id">> }>
    ) => {
      const index = state.filters.findIndex((f) => f.id === action.payload.id);
      if (index !== -1) {
        state.filters[index] = {
          ...state.filters[index],
          ...action.payload.updates,
        };
        // Clear values for empty/not empty operators
        if (
          action.payload.updates.operator === "empty" ||
          action.payload.updates.operator === "not_empty"
        ) {
          state.filters[index].values = [];
        }
      }
    },

    // Remove a filter by ID
    removeFilter: (state, action: PayloadAction<string>) => {
      state.filters = state.filters.filter((f) => f.id !== action.payload);
    },

    // Remove all filters for a specific table
    clearTableFilters: (state, action: PayloadAction<string>) => {
      state.filters = state.filters.filter(
        (f) => f.tableName !== action.payload
      );
    },

    // Remove all filters for a specific table and column
    clearColumnFilters: (
      state,
      action: PayloadAction<{ tableName: string; columnName: string }>
    ) => {
      state.filters = state.filters.filter(
        (f) =>
          !(
            f.tableName === action.payload.tableName &&
            f.columnName === action.payload.columnName
          )
      );
    },

    // Set all filters for a table (replaces existing filters for that table)
    setTableFilters: (
      state,
      action: PayloadAction<{ tableName: string; filters: Omit<TableFilter, "id" | "tableName">[] }>
    ) => {
      // Remove existing filters for this table
      state.filters = state.filters.filter(
        (f) => f.tableName !== action.payload.tableName
      );
      // Add new filters
      const newFilters = action.payload.filters.map((f) => ({
        ...f,
        id: generateFilterId(),
        tableName: action.payload.tableName,
      }));
      state.filters.push(...newFilters);
    },

    // Clear all filters
    clearAllFilters: (state) => {
      state.filters = [];
    },
  },
});

export const {
  addFilter,
  addFilterWithId,
  updateFilter,
  removeFilter,
  clearTableFilters,
  clearColumnFilters,
  setTableFilters,
  clearAllFilters,
} = filterSlice.actions;

export default filterSlice.reducer;

// Selectors
export const selectAllFilters = (state: { filter: FilterState }): TableFilter[] => {
  return state.filter.filters;
};

export const selectFiltersByTable = (
  state: { filter: FilterState },
  tableName: string
): TableFilter[] => {
  return state.filter.filters.filter((f) => f.tableName === tableName);
};

export const selectFiltersByColumn = (
  state: { filter: FilterState },
  tableName: string,
  columnName: string
): TableFilter[] => {
  return state.filter.filters.filter(
    (f) => f.tableName === tableName && f.columnName === columnName
  );
};

export const selectFilterById = (
  state: { filter: FilterState },
  id: string
): TableFilter | undefined => {
  return state.filter.filters.find((f) => f.id === id);
};

// Hook for managing filters for a specific table
export function useTableFilters(tableName: string) {
  const dispatch = useDispatch();
  const filters = useSelector((state: { filter: FilterState }) =>
    selectFiltersByTable(state, tableName)
  );

  const add = useCallback(
    (columnName: string, operator: FilterOperator, values: FilterValue[] = []) => {
      dispatch(addFilter({ tableName, columnName, operator, values }));
    },
    [dispatch, tableName]
  );

  const update = useCallback(
    (id: string, updates: Partial<Omit<TableFilter, "id">>) => {
      dispatch(updateFilter({ id, updates }));
    },
    [dispatch]
  );

  const remove = useCallback(
    (id: string) => {
      dispatch(removeFilter(id));
    },
    [dispatch]
  );

  const clear = useCallback(() => {
    dispatch(clearTableFilters(tableName));
  }, [dispatch, tableName]);

  const setFilters = useCallback(
    (newFilters: Omit<TableFilter, "id" | "tableName">[]) => {
      dispatch(setTableFilters({ tableName, filters: newFilters }));
    },
    [dispatch, tableName]
  );

  // Get filters grouped by column
  const filtersByColumn = useMemo(() => {
    const grouped: Record<string, TableFilter[]> = {};
    for (const filter of filters) {
      if (!grouped[filter.columnName]) {
        grouped[filter.columnName] = [];
      }
      grouped[filter.columnName].push(filter);
    }
    return grouped;
  }, [filters]);

  return {
    filters,
    filtersByColumn,
    add,
    update,
    remove,
    clear,
    setFilters,
  };
}

// Hook for managing a single column's filters
export function useColumnFilters(tableName: string, columnName: string) {
  const dispatch = useDispatch();
  const filters = useSelector((state: { filter: FilterState }) =>
    selectFiltersByColumn(state, tableName, columnName)
  );

  const add = useCallback(
    (operator: FilterOperator, values: FilterValue[] = []) => {
      dispatch(addFilter({ tableName, columnName, operator, values }));
    },
    [dispatch, tableName, columnName]
  );

  const update = useCallback(
    (id: string, updates: Partial<Omit<TableFilter, "id">>) => {
      dispatch(updateFilter({ id, updates }));
    },
    [dispatch]
  );

  const remove = useCallback(
    (id: string) => {
      dispatch(removeFilter(id));
    },
    [dispatch]
  );

  const clear = useCallback(() => {
    dispatch(clearColumnFilters({ tableName, columnName }));
  }, [dispatch, tableName, columnName]);

  return {
    filters,
    add,
    update,
    remove,
    clear,
  };
}

// Hook for accessing all filters across all tables
export function useAllFilters() {
  const dispatch = useDispatch();
  const filters = useSelector((state: { filter: FilterState }) =>
    selectAllFilters(state)
  );

  // Get filters grouped by table
  const filtersByTable = useMemo(() => {
    const grouped: Record<string, TableFilter[]> = {};
    for (const filter of filters) {
      if (!grouped[filter.tableName]) {
        grouped[filter.tableName] = [];
      }
      grouped[filter.tableName].push(filter);
    }
    return grouped;
  }, [filters]);

  const clearAll = useCallback(() => {
    dispatch(clearAllFilters());
  }, [dispatch]);

  return {
    filters,
    filtersByTable,
    clearAll,
  };
}
